import { useState, type ReactNode } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useSession } from "../../app/session";
import { Sidebar } from "./Sidebar";
import { TopBar } from "./TopBar";
import { AiRail } from "./AiRail";
import { ErrorBoundary } from "./ErrorBoundary";
import { Icon } from "../ui/Icon";
import { number } from "../../lib/format";

/**
 * The application frame: navigation, top bar, content, assistant rail.
 *
 * The rail is shown on wide screens only and collapses below 1500px; the
 * sidebar becomes an overlay below 1080px. Nothing is hidden behind a
 * breakpoint that the user cannot get back to.
 */
export function Shell({ rail = true }: { rail?: boolean }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { user } = useSession();

  // The assistant answers from internal records, so portal users do not get the rail.
  const showRail = rail && !user?.isExternal;

  return (
    <div className="shell">
      <Sidebar open={sidebarOpen} onNavigate={() => setSidebarOpen(false)} />

      <div className="shell__main">
        <TopBar onToggleSidebar={() => setSidebarOpen((open) => !open)} />

        <div className={`shell__body${showRail ? " shell__body--rail" : ""}`}>
          <main className="shell__content">
            {/* Keyed by path, so moving to another page always gets a fresh boundary. */}
            <ErrorBoundary key={location.pathname}>
              <Outlet />
            </ErrorBoundary>
          </main>
          {showRail && (
            <ErrorBoundary label="The assistant">
              <AiRail />
            </ErrorBoundary>
          )}
        </div>
      </div>
    </div>
  );
}

/** The mission strip from the approved dashboards, with real counts. */
export function MissionBar({
  stats,
}: {
  stats: { icon: keyof typeof Icon; value: number | string; label: string }[];
}) {
  return (
    <div className="mission">
      <span className="mission__icon">
        <Icon.target width={18} height={18} />
      </span>
      <div className="mission__text">
        <b>Our mission</b>
        <span>
          To create a more connected, sustainable and prosperous world through
          extraordinary events.
        </span>
      </div>
      <div className="mission__stats">
        {stats.map((stat) => {
          const StatIcon = Icon[stat.icon];

          return (
            <div className="mission__stat" key={stat.label}>
              <StatIcon />
              <div>
                <b>{typeof stat.value === "number" ? number(stat.value) : stat.value}</b>
                <span>{stat.label}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** A page section with a heading, used to break long screens into parts. */
export function Section({
  title,
  description,
  actions,
  children,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section style={{ marginTop: 18 }}>
      <div className="row row--between" style={{ marginBottom: 11 }}>
        <div>
          <h2 className="sectiontitle" style={{ marginBottom: description ? 2 : 0 }}>
            {title}
          </h2>
          {description && (
            <p className="muted" style={{ font: "var(--t-small)" }}>
              {description}
            </p>
          )}
        </div>
        {actions}
      </div>
      {children}
    </section>
  );
}
